import { Box, Container, Grid, Paper } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import CircularLoader from '../../../atoms/CircularLoader/circular-loader';
import {
  DebounceTimer,
  LoadingStatus,
  ToastThemes,
} from '../../../constants/common';
import {
  getDateWithTimeZone,
  getFormattedStartOrEndDateForLastMonthFromGivenDate,
  getFormattedValues,
} from '../../../utils/formatter';
import { showToast } from '../../../utils/common.util';
import { GeoTagging } from '../../../services/geoTagging';
import SelfieFormView from './SelfieAndLocation/SelfieFormView';
import ClaimedEmployeeDetails from '../../claim-view/claimed-employee-details';
import TabBar from '../../../atoms/Tabs/TabBar';
import FieldView from '../../molecules/FieldView/FieldView';
import { permissionsObj } from '../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import ActionHeader from '../../OnBoarding/Organization/ActionHeader/ActionHeader';

const AttendanceFormDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(-1);
  const [data, setData] = useState({});
  const { currentUserRoleId } = useSelector((state) => state.main);
  const { timeManagementPermission } = useSelector(
    (state) => state.modulePermissions
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const searchInput = searchParams.get('q');
  const debouncedVal = useDebounce(searchInput, DebounceTimer);
  const location = useLocation();
  const pathNames = location.pathname.split('/');
  const registerId = pathNames[pathNames.length - 1];

  useEffect(async () => {
    if (currentUserRoleId) {
      setLoading(LoadingStatus.Loading);
      await new GeoTagging()
        .listAll(currentUserRoleId, searchInput, null, null, registerId)
        .then((res) => {
          setLoading(LoadingStatus.Success);
          setData(res);
        })
        .catch((err) => {
          showToast(
            err?.response?.data?.message || 'Something Went Wrong! Try Again',
            ToastThemes.error
          );
          setLoading(LoadingStatus.Failure);
        });
    }
  }, [currentUserRoleId, debouncedVal, registerId]);

  const handleDiscard = () => {
    navigate(-1);
  };
  if (!data || typeof data !== 'object') {
    return <div>No data available.</div>; // fallback for safety
  }
  const calendarDate = getFormattedValues(
    'DD/MMM/YYYY',
    data?.attendence?.[0]?.in?.calendarDate ||
      data?.attendence?.[0]?.out?.calendarDate
  );
  const tabs = [
    {
      name: 'attendance',
      label: 'Attendance Form',
      title: 'Attendance Form',
      hide: false,
      panel: (
        <div className="form-view">
          <CircularLoader show={loading === LoadingStatus.Loading} />
          <Box sx={{ backgroundColor: 'white' }}>
            <Box mb={3} mx={-3} mt={-3}>
              <ActionHeader
                labelText={calendarDate}
                showSave={false}
                showBackButton
                onBackClick={handleDiscard}
              />
            </Box>
            <Paper sx={{ mb: 2, p: 2 }}>
              <Grid container rowSpacing={3} columnSpacing={8} pr={8}>
                <Grid item md={3} xs={12}>
                  <FieldView
                    labelText="Work Shift"
                    labelValue={
                      data?.attendence?.[0]?.in?.schedule?.name ||
                      data?.attendence?.[0]?.out?.schedule?.name ||
                      ''
                    }
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <FieldView
                    labelText="Total Hour Worked"
                    labelValue={data?.totalWorkingHours}
                  />
                </Grid>
                {data?.otApplicable && (
                  <Grid item md={3} xs={12}>
                    <FieldView
                      labelText="OT Hours"
                      labelValue={data?.overtime}
                    />
                  </Grid>
                )}
                <Grid item md={3} xs={12}>
                  <FieldView
                    labelText="Status"
                    labelValue={data?.registerStatus}
                  />
                </Grid>
              </Grid>
            </Paper>

            {data?.attendence?.map((entry, index) => {
              const { in: inEntry, out: outEntry } = entry;

              const handleAddClick = (direction, max, min) => {
                if (
                  timeManagementPermission?.['attendance']?.[
                    permissionsObj.allowAdd
                  ]
                ) {
                  let timeZone = '';
                  if (min) {
                    timeZone = min?.attendanceDatetimeTzOffset
                      ?.replace('UTC', '')
                      .trim();
                  }
                  if (max) {
                    timeZone = max?.attendanceDatetimeTzOffset
                      ?.replace('UTC', '')
                      .trim();
                  }

                  navigate(`/time-management/attendance/m?t=${direction}`, {
                    state: {
                      minDate: min
                        ? getDateWithTimeZone(
                            min.attendanceDatetimeUtc,
                            'YYYY-MM-DD HH:mm:ss',
                            timeZone
                          )
                        : getFormattedStartOrEndDateForLastMonthFromGivenDate(
                            calendarDate,
                            'DD/MMM/YYYY',
                            'day',
                            true,
                            'YYYY-MM-DD HH:mm:ss'
                          ) || undefined,
                      maxDate: max
                        ? getDateWithTimeZone(
                            max.attendanceDatetimeUtc,
                            'YYYY-MM-DD HH:mm:ss',
                            timeZone
                          )
                        : getFormattedStartOrEndDateForLastMonthFromGivenDate(
                            calendarDate,
                            'DD/MMM/YYYY',
                            'day',
                            false,
                            'YYYY-MM-DD HH:mm:ss'
                          ) || undefined,
                    },
                  });
                }
              };
              const missing = inEntry ? 'O' : 'I';

              let prevPunch, nextPunch;

              if (missing === 'O') {
                prevPunch = (() => {
                  for (let j = index-1; j >= 0; j--) {
                    if (data?.attendence?.[j]?.out)
                      return getDateWithTimeZone(
                        data?.attendence?.[j].out.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].out?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                    if (data?.attendence?.[j]?.in)
                      return getDateWithTimeZone(
                        data?.attendence?.[j].in.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].in?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                  }
                  return (
                    getFormattedStartOrEndDateForLastMonthFromGivenDate(
                      calendarDate,
                      'DD/MMM/YYYY',
                      'day',
                      true,
                      'YYYY-MM-DD HH:mm:ss'
                    ) || undefined
                  );
                })();

                nextPunch = (() => {
                  for (let j = index+1; j < data?.attendence.length; j++) {
                    if (data?.attendence?.[j]?.in)
                      return getDateWithTimeZone(
                        data?.attendence?.[j].in.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].in?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                    if (data?.attendence?.[j]?.out)
                      getDateWithTimeZone(
                        data?.attendence?.[j].out.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].out?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                  }
                  return (
                    getFormattedStartOrEndDateForLastMonthFromGivenDate(
                      calendarDate,
                      'DD/MMM/YYYY',
                      'day',
                      false,
                      'YYYY-MM-DD HH:mm:ss'
                    ) || undefined
                  );
                })();
              }
              if (missing === 'I') {
                nextPunch = (() => {
                  for (let j = index + 1; j < data?.attendence.length; j++) {
                    if (data?.attendence?.[j]?.out)
                      return getDateWithTimeZone(
                        data?.attendence?.[j].out.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].out?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                    if (data?.attendence[j]?.in)
                      getDateWithTimeZone(
                        data?.attendence[j].in.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence[j].in?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                  }
                  return (
                    getFormattedStartOrEndDateForLastMonthFromGivenDate(
                      calendarDate,
                      'DD/MMM/YYYY',
                      'day',
                      false,
                      'YYYY-MM-DD HH:mm:ss'
                    ) || undefined
                  );
                })();

                prevPunch = (() => {
                  for (let j = index; j >= 0; j--) {
                    if (data?.attendence?.[j]?.in)
                      return getDateWithTimeZone(
                        data?.attendence?.[j].in.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j].in?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                    if (data?.attendence?.[j - 1]?.out)
                      return getDateWithTimeZone(
                        data?.attendence?.[j - 1].out.attendanceDatetimeUtc,
                        'YYYY-MM-DD HH:mm:ss',
                        data?.attendence?.[j - 1].out?.attendanceDatetimeTzOffset
                          ?.replace('UTC', '')
                          .trim()
                      );
                  }
                  return (
                    getFormattedStartOrEndDateForLastMonthFromGivenDate(
                      calendarDate,
                      'DD/MMM/YYYY',
                      'day',
                      true,
                      'YYYY-MM-DD HH:mm:ss'
                    ) || undefined
                  );
                })();
              }
              
              if (!inEntry || !outEntry) {
                // Handle missing punch (show box + form)
                const missing = inEntry ? 'O' : 'I';
                const punchEntry = inEntry || outEntry;

                return (
                  <div
                    key={`missing-${punchEntry?.pkId}`}
                    style={{ display: 'flex', marginBottom: '1rem' }}
                  >
                    {missing === 'I' && (
                      <div
                        className="box"
                        style={{
                          border: '1px dashed rgb(248, 78, 66)',
                          // padding: '0.5rem',
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: '#555',
                          backgroundColor: '#f9f9f9',
                        }}
                        onClick={() =>
                          handleAddClick(
                            'I',
                            punchEntry,
                            data.attendence?.[index - 1]?.out
                          )
                        }
                      >
                        {timeManagementPermission?.['attendance']?.[
                          permissionsObj.allowAdd
                        ]
                          ? 'In +'
                          : 'ACCESS DENIED'}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <SelfieFormView
                        data={punchEntry}
                        bgColor={'rgb(246, 141, 143)'}
                        min={prevPunch}
                        max={nextPunch}
                      />
                    </div>
                    {missing === 'O' && (
                      <div
                        className="box"
                        style={{
                          border: '1px dashed rgb(248, 78, 66)',
                          // padding: '0.5rem',
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: '#555',
                          backgroundColor: '#f9f9f9',
                        }}
                        onClick={() =>
                          handleAddClick(
                            'O',
                            data.attendence?.[index + 1]?.in,
                            punchEntry
                          )
                        }
                      >
                        {timeManagementPermission?.['attendance']?.[
                          permissionsObj.allowAdd
                        ]
                          ? 'Out +'
                          : 'ACCESS DENIED'}
                      </div>
                    )}
                  </div>
                );
              }
              const maxIn = getDateWithTimeZone(
                outEntry?.attendanceDatetimeUtc,
                'YYYY-MM-DD HH:mm:ss',
                outEntry?.attendanceDatetimeTzOffset?.replace('UTC', '').trim()
              );

              const minOut = getDateWithTimeZone(
                inEntry?.attendanceDatetimeUtc,
                'YYYY-MM-DD HH:mm:ss',
                inEntry?.attendanceDatetimeTzOffset?.replace('UTC', '').trim()
              );
              return (
                <div
                  key={`pair-${inEntry.pkId}-${outEntry.pkId}`}
                  style={{ display: 'flex', marginBottom: '1rem' }}
                >
                  <div style={{ flex: 1 }}>
                    <SelfieFormView
                      data={inEntry}
                      bgColor={'#a8e063'}
                      min={prevPunch}
                      max={maxIn || nextPunch}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <SelfieFormView
                      data={outEntry}
                      bgColor={'#a8e063'}
                      min={minOut || prevPunch}
                      max={nextPunch}
                    />
                  </div>
                </div>
              );
            })}
          </Box>
        </div>
      ),
    },
  ];
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container>
        <Grid container>
          <ClaimedEmployeeDetails
            employee={data?.attendence?.[0]?.in?.fkUserId}
            name="Employee"
          />
          <TabBar tabPanels={tabs} disableCreate />;
        </Grid>
      </Container>
    </Box>
  );
};

export default AttendanceFormDashboard;
